import React from "react";
import { Box, Divider, Grid2, IconButton, Paper, Stack } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import FormTitle from "../../../../../../views/components/common/form/FormTitle";
import { EmptyStatePanel, MockupExportPreview } from "./MockupGeneratorSharedUI";
import { EXPORT_CARD_PREVIEW_HEIGHT } from "./mockupGeneratorEditorStyles";
import {
  deriveMockupOutputsFromStoredUrl,
  downloadMockupAsset,
  type MockupSize,
} from "./mockupGeneratorUtils";

type SavedMockup = {
  id: number;
  reference_id: string | null;
  mockup_url: string;
  created_at: string;
};

type Props = {
  preferredSize: MockupSize;
  savedMockups: SavedMockup[];
  mockupOutputs: { large: string; medium: string; small: string } | null;
  mockupReferenceId: string | null;
  deletingGeneratedMockupId: number | null;
  onCopyUrl: (url: string) => void;
  onDeleteMockup: (id: number) => void | Promise<void>;
};

const MockupExportsTab: React.FC<Props> = ({
  preferredSize,
  savedMockups,
  mockupOutputs,
  mockupReferenceId,
  deletingGeneratedMockupId,
  onCopyUrl,
  onDeleteMockup,
}) => {
  const hasExports = savedMockups.length > 0 || Boolean(mockupOutputs?.[preferredSize]);

  if (!hasExports) {
    return (
      <Box
        sx={{
          mt: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <EmptyStatePanel
          title="No Generated Mockups"
          message="No generated mockups available yet."
          minHeight={0}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        pr: 0.5,
      }}
    >
      <Divider sx={{ mb: 2 }} />
      <FormTitle title="Exports" marginBottom={1} />

      {savedMockups.length > 0 ? (
        <>
          <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Saved mockups (recommended size: {preferredSize})
          </CustomTypography>
          <Box sx={{ width: "100%" }}>
            <Grid2 container spacing={2}>
              {savedMockups.map((m) => {
                const urls = deriveMockupOutputsFromStoredUrl(m.mockup_url);
                const url = urls[preferredSize];
                const filename = `${preferredSize}.webp`;
                const created = m.created_at ? String(m.created_at).slice(0, 19) : "";
                return (
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={m.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        height: "100%",
                        minHeight: 300,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CustomTypography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                        {m.reference_id ? `Ref: ${m.reference_id}` : `Export #${m.id}`}
                      </CustomTypography>
                      {created && (
                        <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                          {created}
                        </CustomTypography>
                      )}
                      <MockupExportPreview
                        src={url}
                        alt={`${preferredSize} mockup`}
                        fixedHeight={EXPORT_CARD_PREVIEW_HEIGHT}
                      />
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <IconButton size="small" aria-label="Download" onClick={() => downloadMockupAsset(url, filename)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" aria-label="Copy URL" onClick={() => onCopyUrl(url)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" aria-label="Open">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete"
                          onClick={() => void onDeleteMockup(m.id)}
                          disabled={deletingGeneratedMockupId === m.id}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Grid2>
                );
              })}
            </Grid2>
          </Box>
        </>
      ) : (
        mockupOutputs?.[preferredSize] && (
          <Box>
            {mockupReferenceId && (
              <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Reference: {mockupReferenceId}
              </CustomTypography>
            )}
            <Box sx={{ width: "100%" }}>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                    <CustomTypography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                      {preferredSize}
                    </CustomTypography>
                    <MockupExportPreview
                      src={mockupOutputs[preferredSize]}
                      alt={`${preferredSize} mockup`}
                      fixedHeight={EXPORT_CARD_PREVIEW_HEIGHT}
                    />
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <IconButton
                        size="small"
                        aria-label="Download"
                        onClick={() => downloadMockupAsset(mockupOutputs[preferredSize], `${preferredSize}.webp`)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Copy URL"
                        onClick={() => onCopyUrl(mockupOutputs[preferredSize])}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        component="a"
                        href={mockupOutputs[preferredSize]}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        )
      )}
    </Box>
  );
};

export default MockupExportsTab;
